/**
 * Export Service
 * v4.0 - Handles data export in multiple formats
 */

import { format } from 'date-fns';

export type ExportFormat = 'csv' | 'excel' | 'pdf' | 'json';

interface ExportOptions {
  filename: string;
  data: any[];
  format: ExportFormat;
  metadata?: {
    dateRange?: { startDate: Date; endDate: Date };
    filters?: Record<string, any>;
    generatedAt?: Date;
  };
}

/**
 * Convert data to CSV format
 */
const convertToCSV = (data: any[]): string => {
  if (data.length === 0) return '';

  // Get headers from first object
  const headers = Object.keys(data[0]);
  const csvHeaders = headers.join(',');

  // Convert rows
  const csvRows = data.map(row => {
    return headers.map(header => {
      const value = row[header];
      // Handle values with commas or quotes
      if (typeof value === 'string' && (value.includes(',') || value.includes('"'))) {
        return `"${value.replace(/"/g, '""')}"`;
      }
      return value ?? '';
    }).join(',');
  });

  return [csvHeaders, ...csvRows].join('\n');
};

/**
 * Convert data to JSON format
 */
const convertToJSON = (data: any[], metadata?: any): string => {
  return JSON.stringify({
    data,
    metadata: {
      ...metadata,
      generatedAt: new Date().toISOString(),
      recordCount: data.length
    }
  }, null, 2);
};

/**
 * Download file to user's computer
 */
const downloadFile = (content: string, filename: string, mimeType: string) => {
  const blob = new Blob([content], { type: mimeType });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

/**
 * Export data to CSV
 */
export const exportToCSV = async (options: ExportOptions): Promise<void> => {
  const { filename, data, metadata } = options;

  if (data.length === 0) {
    throw new Error('No data to export');
  }

  const csv = convertToCSV(data);
  const timestamp = format(new Date(), 'yyyyMMdd_HHmmss');
  const fullFilename = `${filename}_${timestamp}.csv`;

  downloadFile(csv, fullFilename, 'text/csv;charset=utf-8;');
};

/**
 * Export data to JSON
 */
export const exportToJSON = async (options: ExportOptions): Promise<void> => {
  const { filename, data, metadata } = options;

  if (data.length === 0) {
    throw new Error('No data to export');
  }

  const json = convertToJSON(data, metadata);
  const timestamp = format(new Date(), 'yyyyMMdd_HHmmss');
  const fullFilename = `${filename}_${timestamp}.json`;

  downloadFile(json, fullFilename, 'application/json;charset=utf-8;');
};

/**
 * Export data to Excel (using CSV for now, can be enhanced with xlsx library)
 */
export const exportToExcel = async (options: ExportOptions): Promise<void> => {
  const { filename, data, metadata } = options;

  if (data.length === 0) {
    throw new Error('No data to export');
  }

  const csv = convertToCSV(data);
  const timestamp = format(new Date(), 'yyyyMMdd_HHmmss');
  const fullFilename = `${filename}_${timestamp}.xlsx`;

  // For now, export as CSV with .xlsx extension
  // Can be enhanced with xlsx library for true Excel format
  downloadFile(csv, fullFilename, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
};

/**
 * Export data to PDF (placeholder - requires pdf generation library)
 */
export const exportToPDF = async (options: ExportOptions): Promise<void> => {
  const { filename, data, metadata } = options;

  if (data.length === 0) {
    throw new Error('No data to export');
  }

  // TODO: Implement PDF export with jsPDF or similar
  // For now, show alert
  alert('PDF export coming soon! Please use CSV or JSON export for now.');
  throw new Error('PDF export not yet implemented');
};

/**
 * Main export function - routes to appropriate export method
 */
export const exportData = async (options: ExportOptions): Promise<void> => {
  const { format } = options;

  switch (format) {
    case 'csv':
      return exportToCSV(options);
    case 'json':
      return exportToJSON(options);
    case 'excel':
      return exportToExcel(options);
    case 'pdf':
      return exportToPDF(options);
    default:
      throw new Error(`Unsupported export format: ${format}`);
  }
};

/**
 * Prepare report data for export
 * Flattens nested objects and formats values
 */
export const prepareDataForExport = (data: any[]): any[] => {
  return data.map(item => {
    const flattened: Record<string, any> = {};

    Object.keys(item).forEach(key => {
      const value = item[key];

      // Handle dates
      if (value instanceof Date) {
        flattened[key] = format(value, 'yyyy-MM-dd HH:mm:ss');
      }
      // Handle nested objects (flatten one level)
      else if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
        Object.keys(value).forEach(nestedKey => {
          flattened[`${key}_${nestedKey}`] = value[nestedKey];
        });
      }
      // Handle arrays (convert to string)
      else if (Array.isArray(value)) {
        flattened[key] = value.join('; ');
      }
      // Handle primitives
      else {
        flattened[key] = value;
      }
    });

    return flattened;
  });
};
